import { type FC, useRef, useEffect } from 'react';
import { graphql, useFragment } from 'react-relay';
import { filterNulls } from 'dibs-ts-utils/exports/filterNulls.ts';
import PhotoSwiper from 'dibs-interiors-photos/exports/PhotoSwiper.tsx';
import { type Photo } from 'dibs-interiors-photos/exports/types.ts';
import PhotoContainer from 'dibs-interiors-photos/exports/PhotoContainer.tsx';

import { type PhotoSwiperWrapper_photoNode$key } from './__generated__/PhotoSwiperWrapper_photoNode.graphql.ts';

type Props = {
    photoNode: PhotoSwiperWrapper_photoNode$key | null | undefined;
    onPageChange: (pageUrl: string) => void;
    photos: Photo[];
    photoIndex: number;
    photoId: string;
};

const PhotoSwiperWrapper: FC<Props> = props => {
    const { photos, onPageChange, photoNode: photoNodeRef, photoIndex, photoId } = props;

    const photoNode = useFragment(
        graphql`
            fragment PhotoSwiperWrapper_photoNode on TradePhotosQueryOutput {
                photo {
                    path
                    height
                    width
                    serviceId
                    name
                    url
                }
            }
        `,
        photoNodeRef
    );

    const photo = photoNode?.photo;

    // avoid flashing photo in swiper when all project photos are finished loading
    const hasPreLoadedAllPhotos = photos.length > 0;
    const initialSwiperPhotoIdRef = useRef<string | null>(null);
    useEffect(() => {
        if (initialSwiperPhotoIdRef.current === null && !hasPreLoadedAllPhotos && photoId) {
            initialSwiperPhotoIdRef.current = photoId;
        } else if (hasPreLoadedAllPhotos && photoId !== initialSwiperPhotoIdRef.current) {
            initialSwiperPhotoIdRef.current = null;
        }
    }, [hasPreLoadedAllPhotos, photoId]);

    if (!photo?.path) {
        return null;
    }

    const onSwiperPageChange = (nextPage: number): void => {
        const photoUrl = photos[nextPage]?.url || '';
        onPageChange(photoUrl);
    };

    // show initial photo while others are loading
    const swiperPhotos = [...(hasPreLoadedAllPhotos ? photos : [photo])].filter(filterNulls);

    return (
        <>
            <PhotoSwiper
                hasInfiniteLoop
                key={photoIndex}
                startingPage={photoIndex}
                onPageChange={onSwiperPageChange}
            >
                {swiperPhotos.map((_photo, index) => {
                    return (
                        <PhotoContainer
                            key={_photo.serviceId || index}
                            shouldShowImage={photoIndex === -1 || index === photoIndex}
                            forceOpacity={
                                initialSwiperPhotoIdRef.current === _photo.serviceId || index === 0
                            }
                            photo={_photo}
                            index={index}
                        />
                    );
                })}
            </PhotoSwiper>
        </>
    );
};

export default PhotoSwiperWrapper;
